import { useState, type ReactNode } from "react";
import { texte } from "../../texte";

function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div
      className="overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="mh">
      <h2>{title}</h2>
      <button className="x" onClick={onClose} aria-label={texte.dialogs.close}>
        ×
      </button>
    </div>
  );
}

/** Parst "#/ws/<token>/<workspace>" (mit optionalem ?expert=1) aus einem eingefügten Link/Code. */
export function parseAccessLink(raw: string): { token: string; workspace: string; expert: boolean } | null {
  const m = raw.trim().match(/#\/ws\/([^/?]+)\/([^/?]+)(?:\?([^#]*))?$/);
  if (!m) return null;
  const query = new URLSearchParams(m[3] ?? "");
  return { token: decodeURIComponent(m[1]), workspace: decodeURIComponent(m[2]), expert: query.get("expert") === "1" };
}

export function JoinDialog({
  onClose,
  onRedeem,
}: {
  onClose: () => void;
  onRedeem: (link: { token: string; workspace: string; expert: boolean }) => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  function submit() {
    const parsed = parseAccessLink(value);
    if (!parsed) {
      setError(true);
      return;
    }
    onRedeem(parsed);
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div className="modal">
        <ModalHeader title={texte.dialogs.join.title} onClose={onClose} />
        <div className="mb">
          <div className="field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="label">{texte.dialogs.join.label}</span>
            <input
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(false);
              }}
              style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}
              autoFocus
            />
          </div>
          {error && (
            <p className="hint" style={{ marginTop: 10, color: "var(--err)" }}>
              {texte.dialogs.join.unknownTokenHint}
            </p>
          )}
          <p className="hint" style={{ marginTop: 10 }}>
            {texte.dialogs.join.hint}
          </p>
        </div>
        <div className="mf">
          <button className="btn" onClick={onClose}>
            {texte.dialogs.join.cancel}
          </button>
          <button className="btn primary" onClick={submit}>
            {texte.dialogs.join.submit}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export function ShareDialog({
  workspace,
  link,
  onClose,
  onCopied,
}: {
  workspace: string;
  link: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  return (
    <Overlay onClose={onClose}>
      <div className="modal">
        <ModalHeader title={`${texte.dialogs.share.titlePrefix} „${workspace}“`} onClose={onClose} />
        <div className="mb">
          <p style={{ marginBottom: 12 }}>{texte.dialogs.share.body}</p>
          <div className="share-code">{link}</div>
          <p className="hint" style={{ marginTop: 10 }}>
            {texte.dialogs.share.hint}
          </p>
        </div>
        <div className="mf">
          <button
            className="btn primary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(link);
              } catch {
                /* Zwischenablage evtl. nicht erlaubt — der Link steht trotzdem sichtbar da. */
              }
              onClose();
              onCopied();
            }}
          >
            {texte.dialogs.share.copyClose}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export function VoucherDialog({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (amount: number) => void;
}) {
  const [value, setValue] = useState("150.000");

  function submit() {
    const amount = Number(value.replace(/[.\s]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) return;
    onSubmit(amount);
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div className="modal">
        <ModalHeader title={texte.dialogs.voucher.title} onClose={onClose} />
        <div className="mb">
          <div className="field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="label">{texte.dialogs.voucher.amountLabel}</span>
            <input type="text" className="num" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
          <p className="hint" style={{ marginTop: 10 }}>
            {texte.dialogs.voucher.hint}
          </p>
        </div>
        <div className="mf">
          <button className="btn" onClick={onClose}>
            {texte.dialogs.voucher.cancel}
          </button>
          <button className="btn primary" onClick={submit}>
            {texte.dialogs.voucher.submit}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
